<script setup lang="ts">
import { ref, onMounted, computed, nextTick, watch, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useNotificationStore } from '@/stores/notification'
import { ElLoading, ElMessage } from 'element-plus'
import NavBar from './components/layout/NavBar.vue'
import Footer from './components/layout/Footer_new.vue'
import FloatingButtons from './components/common/FloatingButtons.vue'
import LoginDialog from './components/LoginDialog.vue'
import RegisterDialog from './components/RegisterDialog.vue'
// 引入自定义认证状态管理
import authState from './utils/authState'
// 引入登录弹窗状态管理
import authDialog from './utils/authDialog'
import registerDialog from './utils/registerDialog'
// 引入事件总线
import eventBus, { EventTypes } from './utils/eventBus'
import {
  House, Picture, Edit, SetUp, 
  Money, Monitor, User, Menu as IconMenu
} from '@element-plus/icons-vue'

const backendUrl = ref(import.meta.env.VITE_JAVA_API_URL)


const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const notificationStore = useNotificationStore()
const isReady = ref(false)

// 登录弹窗状态
const loginDialogVisible = ref(false)
const loginRedirectUrl = ref('')
// 注册弹窗状态
const RegisterDialogVisible = ref(false)
const RegisterRedirectUrl = ref('')

// 定义类型
interface TokenExpiredEvent {
  code: string;
  message: string;
  originalRequest?: any;
}

interface LoginResult {
  user: any;
  isAdmin: boolean;
  redirectUrl?: string;
}

// 监听全局登录弹窗状态
watch(() => {
  const state = authDialog.getState();
  return {
    visible: state.visible,
    redirectUrl: state.redirectUrl
  };
}, (newValue) => {
  console.log('App检测到authDialog状态变化:', newValue.visible)
  loginDialogVisible.value = newValue.visible
  loginRedirectUrl.value = newValue.redirectUrl
}, { immediate: true })

// 监听全局注册弹窗状态
watch(() => {
  const state = registerDialog.getState();
  return {
    visible: state.visible,
    redirectUrl: state.redirectUrl
  };
}, (newValue) => {
  console.log('App检测到authDialog状态变化:', newValue.visible)
  RegisterDialogVisible.value = newValue.visible
  RegisterRedirectUrl.value = newValue.redirectUrl
}, { immediate: true })

// 计算属性
const isAdminRoute = computed(() => route.path.startsWith('/admin'))
const isLoggedIn = computed(() => authState.isLoggedIn.value) // 使用authState

onMounted(async () => {
  console.log('App 挂载 - 初始化状态')
  
  try {
    // 使用authState直接获取登录状态
    // authState在创建时会自动从localStorage加载数据
    isReady.value = true
    console.log('应用准备完毕 - 当前登录状态:', 
      authState.isLoggedIn.value, 
      '本地token存在:', !!localStorage.getItem('token'))
    
    // 记录网站访问（基于会话）
    recordSessionVisit();
    
    // 页面可见性变化时的处理
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 注册令牌失效事件监听
    eventBus.on(EventTypes.AUTH_TOKEN_EXPIRED, handleTokenExpired)
  } catch (error) {
    console.error('初始化失败:', error)
    // 即使出错也设置应用为就绪状态
    isReady.value = true
  }
})

// 处理页面可见性变化
const handleVisibilityChange = () => {
  // 当页面从隐藏变为可见时，可能是新的会话
  if (document.visibilityState === 'visible') {
    checkAndRecordSession();
  }
};

// 基于会话的访问记录函数
const recordSessionVisit = async () => {
  try {
    // 获取当前会话ID
    let sessionId = localStorage.getItem('visit_session_id');
    const lastVisitTime = localStorage.getItem('last_visit_time');
    const now = Date.now();
    
    // 检查会话是否过期（30分钟无活动视为新会话）
    const sessionExpired = !lastVisitTime || (now - parseInt(lastVisitTime)) > 30 * 60 * 1000;
    
    // 如果没有会话ID或会话过期，创建新会话ID
    if (!sessionId || sessionExpired) {
      sessionId = generateSessionId();
      localStorage.setItem('visit_session_id', sessionId);
      console.log('创建新会话:', sessionId);
    }
    
    // 更新最后访问时间
    localStorage.setItem('last_visit_time', now.toString());
    
    // 向服务器发送访问记录，带上会话ID
    // const response = await fetch('/api/admin/dashboard/record-visit', {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //     'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
    //   },
    //   body: JSON.stringify({ 
    //     userId: userStore.user?.id || null,
    //     sessionId: sessionId
    //   })
    // });
    
    // if (response.ok) {
    //   console.log('会话访问已记录');
    // } else {
    //   console.warn('记录会话访问请求失败，状态码:', response.status);
    // }
  } catch (error: any) {
    // 静默失败，只记录日志，不影响用户体验
    console.error('记录会话访问失败:', error.message || error);
  }
};

// 生成唯一会话ID
const generateSessionId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

// 检查并记录会话
const checkAndRecordSession = () => {
  const lastVisitTime = localStorage.getItem('last_visit_time');
  const now = Date.now();
  
  // 如果超过30分钟没有活动，视为新会话
  if (!lastVisitTime || (now - parseInt(lastVisitTime)) > 30 * 60 * 1000) {
    console.log('会话已过期，记录新会话');
    recordSessionVisit();
  } else {
    // 只更新最后访问时间，不发送新请求
    localStorage.setItem('last_visit_time', now.toString());
    console.log('会话有效，更新访问时间');
  }
};

// 监听路由变化，记录日志
watch(() => route.path, () => {
  console.log('路由变化:', route.path, '登录状态:', authState.isLoggedIn.value)
})

const handleLogout = async () => {
  console.log('执行登出操作')
  
  try {
    // 使用authState登出
    authState.logout()
    
    // 使用 nextTick 确保视图更新
    await nextTick()
  } catch (error) {
    console.error('登出过程中出错:', error)
    // 确保本地存储被清除
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  }
  
  console.log('登出后重定向到首页')
  router.push('/')
}

// 登录成功处理
const handleLoginSuccess = (result: LoginResult) => {
  console.log('登录成功:', result)
  authDialog.onLoginSuccess(result)
}

// 登录错误处理
const handleLoginError = (error: Error) => {
  console.error('登录错误:', error)
}


// 登录成功处理
const handleRegisterSuccess = (result: LoginResult) => {
  console.log('登录成功:', result)
  authDialog.onLoginSuccess(result)
}

// 登录错误处理
const handleRegisterError = (error: Error) => {
  console.error('登录错误:', error)
}

// 处理令牌失效事件
const handleTokenExpired = (data: TokenExpiredEvent) => {
  console.log('检测到令牌失效事件:', data)
  
  // 如果用户当前已登录，执行强制登出并显示登录弹窗
  if (isLoggedIn.value) {
    // 首先强制登出以确保清除所有登录状态
    authState.logout()
    
    // 确保视图更新
    nextTick(() => {
        // 显示登录弹窗，并设置重定向URL
      authDialog.show({ 
        redirectUrl: router.currentRoute.value.fullPath,
        callback: () => {
          ElMessage.success('重新登录成功')
        }
      })
    })
    
    // 移除admin页面强制重定向到首页的逻辑
    // 登录弹窗会使用redirectUrl自动跳转到原页面
  }
}

// 清理事件监听
onBeforeUnmount(() => {
  // 移除事件监听
  eventBus.off(EventTypes.AUTH_TOKEN_EXPIRED, handleTokenExpired)
  // 移除可见性变化监听
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<template>
  <div class="app">
    <NavBar 
      v-if="isReady && !isAdminRoute" 
      :is-logged-in="isLoggedIn" 
      @logout="handleLogout" 
    >
      <el-menu-item index="/monetize" :route="{ path: '/monetize' }">
        <el-icon><Money /></el-icon>
        <span>变现渠道</span>
      </el-menu-item>
      <el-menu-item index="/ai-tool" :route="{ path: '/ai-tool' }">
        <el-icon><Monitor /></el-icon>
        <span>简单画</span>
      </el-menu-item>
    </NavBar>
    <div class="main-content">
      <router-view v-if="isReady" />
      <div v-else class="loading">
        <div class="el-loading-mask" style="z-index: 2000;">
          <div class="el-loading-spinner">
            <svg class="circular" viewBox="25 25 50 50">
              <circle class="path" cx="50" cy="50" r="20" fill="none"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
    <!-- 页脚 -->
    <Footer v-if="isReady && !isAdminRoute" />
    <!-- 联系客服按钮 -->
    <FloatingButtons v-if="isReady && !isAdminRoute" />
    
    <!-- 全局登录弹窗 -->
    <LoginDialog 
      v-model:show="loginDialogVisible"
      :redirect-url="loginRedirectUrl"
      @login-success="handleLoginSuccess"
      @login-error="handleLoginError"
    />
    <!-- 全局注册弹窗 -->
    <RegisterDialog
      v-model:show="RegisterDialogVisible"
      :redirect-url="RegisterRedirectUrl"
      @login-success="handleRegisterSuccess"
      @login-error="handleRegisterError"
    />
  </div>
</template>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
}

body {
  background-color: #f5f5f5;
}

.app {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  /* padding-top: 60px; */
  display: flex;
  flex-direction: column;
  background-color: #fff;
}

.loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
}
</style>
